import {
  SpanKind, SpanStatusCode, context, trace,
} from '@opentelemetry/api';
import { Amplify } from 'aws-amplify';
import { AuthError, fetchAuthSession } from 'aws-amplify/auth';
import {
  Environment, Network, RecordSource, Store,
} from 'relay-runtime';

import { APIConfig } from 'src/config';
import { tracingState, withTracingOptions } from 'src/tracing';

/**
 * Update intercom if there is a route change in the application
 *
 */
const handleIntercom = () => {
  if (window.Intercom) {
    window.Intercom('update');
  }
};

// Amplify v6's token orchestrator identifies these errors as terminal session
// failures, clears its local tokens, and requires reauthentication. The names
// are not part of Amplify's public export surface, so they are maintained here.
const REAUTHENTICATION_EXCEPTION_NAMES = [
  'UserUnAuthenticatedException',
  'NotAuthorizedException',
  'TokenRevokedException',
  'UserNotFoundException',
  'PasswordResetRequiredException',
  'UserNotConfirmedException',
  'RefreshTokenReuseException',
];

/**
 * Determines whether a `fetchAuthSession()` rejection requires the user to
 * reauthenticate, as opposed to a token-refresh network/service failure or an
 * Amplify misconfiguration.
 *
 * The check is layered so it stays robust across SDK changes rather than
 * depending on a single hardcoded string:
 * 1. Case-insensitive prefix match against the terminal Cognito exception names
 *    used by Amplify's token orchestrator.
 * 2. Fallback: an `AuthError` (the publicly exported Amplify error type) whose
 *    name still references being unauthenticated. `instanceof` alone is too
 *    broad (it covers every auth error) and can be unreliable across bundled
 *    module boundaries, so it only corroborates the name-based signal.
 * @param {unknown} error - the rejection thrown by `fetchAuthSession()`
 * @returns {boolean} `true` when the error requires user reauthentication
 */
export function isUnauthenticatedError(error) {
  if (!error) {
    return false;
  }
  const name = typeof error.name === 'string' ? error.name.toLowerCase() : '';
  if (REAUTHENTICATION_EXCEPTION_NAMES.some(
    (exceptionName) => name.startsWith(exceptionName.toLowerCase()),
  )) {
    return true;
  }
  return error instanceof AuthError && name.includes('unauthenticated');
}

/**
 * Determines whether Amplify has the Cognito user pool configuration required to
 * distinguish a signed-out user from an unavailable authentication service.
 * @returns {boolean} `true` when both required Cognito identifiers are configured
 */
function isCognitoUserPoolConfigured() {
  const cognitoConfig = Amplify.getConfig()?.Auth?.Cognito;
  return Boolean(cognitoConfig?.userPoolId && cognitoConfig?.userPoolClientId);
}

/**
 * Helper function to get the current session, with tracing
 * @returns {object} - session object
 */
export function getCurrentSession() {
  return withTracingOptions('Auth.currentSession', { kind: SpanKind.CLIENT }, () => fetchAuthSession()
    .then((sess) => {
      console.log({ sess });
      return sess;
    })
    .catch((e) => {
      const span = trace.getSpan(context.active());
      console.error('getCurrentSession error', e, span);
      if (span) {
        span.recordException(e);
        span.setStatus({
          code: SpanStatusCode.ERROR,
          message: (e && (e.stack || e.message)) || String(e),
        });
      }
      // Amplify v6 rejects when no user is signed in or a token-refresh failure
      // requires reauthentication. Map those terminal session errors to 401 so
      // the route error handler redirects to /login; preserve status 0 for
      // transient refresh, network, and configuration failures so AuthErrorPage
      // can render instead.
      const err = e instanceof Error ? e : new Error(String(e));
      err.status = err.status ?? (isUnauthenticatedError(e) ? 401 : 0);
      throw err;
    }));
}

/**
 * Wrap the function that fetches the results of an operation (query/mutation/etc)
 * and returns its results as a Promise using a span
 * @param {object} operation - (query/mutation/etc)
 * @param {object} variables - query variables
 * @returns {object} - api response object
 */
function fetchQueryRaw(
  operation,
  variables,
  // cacheConfig,
  // uploadables,
) {
  let token;
  return getCurrentSession()
    .then((sess) => {
      if (sess && sess.tokens) {
        token = sess.tokens.accessToken?.toString();
        // // Another, alternative hacky way to get the user if
        // if (sess.tokens.idToken && sess.tokens.idToken.payload
        //   && sess.tokens.idToken.payload['custom:id']) {
        //   span.setAttribute('user_id', sess.tokens.idToken.payload['custom:id']);
        // }
        const req = {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            query: operation.text, // GraphQL text from input
            variables,
          }),
        };
        // Add authentication
        if (token) {
          req.headers.Authorization = `Bearer ${token}`;
        }

        let url = APIConfig().GRAPH_URL;
        if (!url) {
          // TODO: get the base rule and concatenate
          url = 'http://0.0.0.0:5678/api/v2';
        }

        handleIntercom();

        let status;
        return fetch(url, req)
          .then((response) => {
            status = response.status;
            return response.json();
          })
          .then((response) => {
            const span = trace.getSpan(context.active());
            if (status >= 400) {
              if (span) {
                span.setStatus({ code: SpanStatusCode.ERROR, message: 'error getting json' });
              }
              const err = new Error(`HTTP error ${status}`);
              err.status = status;
              throw err;
            }
            const { data, errors } = response;
            if (errors) {
              if (span) {
                span.recordException(errors);
                span.setStatus({ code: SpanStatusCode.ERROR, message: 'fetch error' });
              }
              if (Object.keys(data).reduce((acc, d) => (acc && (data[d] === null)), true)) {
                return { data: null, errors };
              }
            }
            // A bit of a hack to populate the tracing state as early as possible
            if (data && data.viewer && data.viewer.viewerUser) {
              tracingState.setUserID(data.viewer.viewerUser.id);
            }
            return { data, errors };
          })
          .catch((error) => {
            console.error('fetchQueryRaw.fetch error', error);
            const span = trace.getSpan(context.active());
            if (span) {
              span.recordException(error);
              span.setStatus({ code: SpanStatusCode.ERROR, message: 'fetch error' });
            }
            // Preserve the HTTP status so callers can distinguish a server or
            // network failure (500, or 0 for an unreachable API) from an auth
            // failure. An empty-body 500 keeps 500; a transport failure is 0.
            const err = error;
            err.status = err.status ?? status ?? 0;
            throw err;
          });
      }
      // An unconfigured/incomplete Cognito provider can also resolve without
      // tokens. That cannot be recovered by redirecting to a login page that
      // needs the same configuration, so expose it as a non-auth failure.
      if (!isCognitoUserPoolConfigured()) {
        const err = new Error('Amplify Cognito configuration is unavailable');
        err.status = 0;
        throw err;
      }

      // With Cognito configured, no authenticated session/token is an auth
      // failure and should redirect to login instead of returning a status-less,
      // malformed Relay "No data returned" error.
      const err = new Error('No authenticated session');
      err.status = 401;
      throw err;
    });
}

/**
 * Wrap the function that fetches the results of an operation (query/mutation/etc)
 * and returns its results as a Promise using a span
 * @param {object} operation -  (query/mutation/etc)
 * @param {object} variables - query variables
 * @returns {object} - promise (using a span)
 */
export function fetchQuery(
  operation,
  variables,
  // cacheConfig,
  // uploadables,
) {
  return withTracingOptions('fetchQuery', { kind: SpanKind.CLIENT }, fetchQueryRaw, operation, variables);
}

/**
 * Description
 * @param {object} operation
 * @param {object} request
 * @param {Function} onCompleted
 * @param {Function} onError
 * @returns {object} - api response object
 */
export function fetchREST(operation, request, onCompleted, onError) {
  return withTracingOptions('fetchREST', { kind: SpanKind.CLIENT }, () => {
    const httpReq = {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
      },
      body: request,
    };
    const url = `${APIConfig().REST_BASE_URL}/${operation}`;
    const span = trace.getSpan(context.active());

    if (span) {
      span.setAttribute('operation', operation);
    }
    let status;
    fetch(url, httpReq)
      .then((response) => {
        status = response.status;
        return response.json();
      })
      .then((body) => {
        if (status >= 400) {
          if (span) {
            span.setStatus({ code: SpanStatusCode.ERROR, message: 'error getting json' });
          }
          onError(body);
        } else {
          if (span) {
            span.setStatus({ code: SpanStatusCode.OK });
          }
          onCompleted(body);
        }
      })
      .catch((error) => {
        console.log({ operation, url, error });
        if (span) {
          span.recordException(error);
          span.setStatus({ code: SpanStatusCode.ERROR, message: 'error getting json' });
        }
        onError(error);
      })
      .finally(() => {
        if (span) {
          tracingState.addToSpan(span)
            .then(() => span.end())
            .catch((e) => console.error('error adding to span', e));
        }
      });
  });
}

// Create a network layer from the fetch function
const network = Network.create(fetchQuery);
const store = new Store(new RecordSource());

const environment = new Environment({
  network,
  store,
  // ... other options
});

export default environment;
